import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Stack, 
  Typography,
  Chip
} from '@mui/material';
import { PersonOutline } from '@mui/icons-material';
import { useGetRegistrationQuery } from '../../../../services/RegistrationService';
import { useAppSelector } from '../../../../app/store';
import { SectionHeader, SectionPaper } from '../../../common/components';

const PatientInfoView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const mrn = searchParams.get('mrn') || '';

  // Get encounter state from Redux
  const encounter = useAppSelector((state) => state.encounter.currentEncounter);

  // Create concatenated ID for API call when MRN is provided
  const registrationId = mrn ? `${company}-${center}-${mrn}` : '';

  // Use RTK Query to fetch registration data when MRN is provided
  const { data: registrationData } = useGetRegistrationQuery(
    { id: registrationId }, 
    {
      skip: !mrn || !company || !center,
    }
  );

  // Format date from API format to display format
  const formatDateForDisplay = (dateString: string | undefined) => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  // Get the date of birth - prioritize registration data if available, otherwise use encounter data
  const getDateOfBirth = () => {
    if (registrationData?.demographics?.dateOfBirth) {
      return formatDateForDisplay(registrationData.demographics.dateOfBirth);
    }
    return formatDateForDisplay(encounter?.patient?.birthDate);
  };

  // Helper component for displaying field values
  const InfoField: React.FC<{ label: string; value: string | undefined; flex?: number }> = ({ label, value, flex = 1 }) => (
    <Box sx={{ flex }}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>
        {value || 'Not provided'}
      </Typography>
    </Box>
  );

  // Helper for gender display
  const getGenderDisplay = (sex: string | undefined) => {
    switch (sex) {
      case 'M': return 'Male';
      case 'F': return 'Female';
      case 'O': return 'Other';
      default: return 'Not specified';
    }
  };

  return (
    <Box>
      <SectionHeader 
        icon={<PersonOutline sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} 
        title="Patient Information" 
      />
      <SectionPaper>
        {/* Patient Identity */}
        <Stack direction="row" gap={1} sx={{ mb: 1 }}>
          <InfoField 
            label="MRN" 
            value={encounter?.patient?.mrn} 
            flex={1}
          />
          <InfoField 
            label="Full Name" 
            value={`${encounter?.patient?.firstName || ''} ${encounter?.patient?.middleName || ''} ${encounter?.patient?.lastName || ''}`.trim().replace(/\s+/g, ' ') || 'Not provided'}
            flex={1}
          />
          <InfoField 
            label="Date of Birth" 
            value={getDateOfBirth()} 
            flex={1}
          />
        </Stack>
        
        {/* Contact and Demographics */}
        <Stack direction="row" gap={1}>
          <InfoField 
            label="Phone Number" 
            value={encounter?.patient?.homePhone} 
            flex={1}
          />
          <InfoField 
            label="Email Address" 
            value={encounter?.patient?.email} 
            flex={1}
          />
          <InfoField 
            label="Gender" 
            value={getGenderDisplay(encounter?.patient?.sex)} 
            flex={1}
          />
        </Stack>
      </SectionPaper>
    </Box>
  );
};

export default PatientInfoView;
